import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { USER_API } from "config";
import { ApiErrorCode } from "config/errorCodes";
import { useAxios } from "hooks/useAxios";
import { useStepUpProtectedAction } from "hooks/auth";
import { workspaceKeys } from "queries";
import { handleToast, ServerError } from "utils";
import { getToastMessageForError } from "utils/errorHandling";

export type ClaudeConnectorStatus =
  | { connected: false }
  | {
      connected: true;
      api_key_id: string;
      name: string;
      created_at: string;
      expires_at: string | null;
      first_used_at: string | null;
      last_used_at: string | null;
      scopes: string[];
    };

export type ClaudeConnectorCreated = {
  api_key_id: string;
  connector_url: string;
  claude_install_url: string;
  claude_admin_install_url: string;
  claude_code_command: string;
  expires_at: string | null;
  scopes: string[];
  name: string;
  created_at: string;
  first_used_at: string | null;
  last_used_at: string | null;
};

const isReauthenticationCancelled = (error: unknown): boolean =>
  error instanceof Error && error.message === "Re-authentication cancelled";

const toastForConnectorError = (error: unknown, fallback: string): void => {
  if (isReauthenticationCancelled(error) || isClaudeConnectorLicenseError(error)) {
    return;
  }
  const serverError = isAxiosError(error)
    ? ServerError.fromAxiosError(error)
    : error instanceof ServerError
      ? error
      : null;
  handleToast(serverError ? getToastMessageForError(serverError) : fallback, "error");
};

export const isClaudeConnectorLicenseError = (error: unknown): boolean => {
  if (error instanceof ServerError) {
    return error.code === ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED;
  }
  if (!isAxiosError(error)) {
    return false;
  }
  const serverError = ServerError.fromAxiosError(error);
  if (serverError.code === ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED) {
    return true;
  }
  const data = error.response?.data as { code?: string } | undefined;
  return data?.code === ApiErrorCode.CLAUDE_CONNECTOR_LICENSE_REQUIRED;
};

export const useClaudeConnector = (workspaceId: string | undefined) => {
  const axios = useAxios({ allResponse: true });
  const queryClient = useQueryClient();
  const { runProtectedAction } = useStepUpProtectedAction();
  const enabled = Boolean(workspaceId);

  const statusQuery = useQuery<ClaudeConnectorStatus>({
    queryKey: workspaceKeys.claudeConnector(workspaceId ?? ""),
    queryFn: async () => {
      const response = await axios.get<{ data: ClaudeConnectorStatus }>(
        USER_API.getWorkspaceClaudeConnector(workspaceId!)
      );
      return response.data.data;
    },
    enabled,
    refetchOnWindowFocus: true,
    refetchInterval: (data) => {
      if (data?.connected && !data.first_used_at) {
        return 5000;
      }
      return false;
    },
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.claudeConnector(workspaceId ?? ""),
    });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.apiKeys(workspaceId ?? "", "mine"),
    });
    void queryClient.invalidateQueries({
      queryKey: workspaceKeys.apiKeys(workspaceId ?? "", "all"),
    });
  };

  const createMutation = useMutation({
    mutationFn: async () =>
      runProtectedAction(async () => {
        const response = await axios.post<{ data: ClaudeConnectorCreated }>(
          USER_API.createWorkspaceClaudeConnector(workspaceId!)
        );
        return response.data.data;
      }),
    onSuccess: () => {
      invalidate();
    },
    onError: (error: unknown) => {
      toastForConnectorError(error, "Could not connect to Claude.");
    },
  });

  const disconnectMutation = useMutation({
    mutationFn: async () =>
      runProtectedAction(async () => {
        await axios.delete(USER_API.deleteWorkspaceClaudeConnector(workspaceId!));
      }),
    onSuccess: () => {
      invalidate();
      handleToast("Claude connector disconnected.", "success");
    },
    onError: (error: unknown) => {
      toastForConnectorError(error, "Could not disconnect Claude.");
    },
  });

  return {
    statusQuery,
    createMutation,
    disconnectMutation,
    isCreating: createMutation.isPending,
    isDisconnecting: disconnectMutation.isPending,
  };
};
